// Client-safe status vocabulary for `southpaw_jobs.status` (a plain text
// column), shared by the send pipeline, the jobs APIs, the analytics sync and
// every dashboard that buckets jobs as sent / not sent.

import dayjs, { type Dayjs } from "@punchup/utils/dayjs";

import { PLAIN_BODY_EMAIL_TEMPLATE } from "./unified-messaging.types";

export const SOUTHPAW_JOB_COMPLETED_WITH_ERRORS = "completed_with_errors";

/**
 * Statuses of a job whose send was handed off — recipients have the message or
 * will shortly, so the job is treated as sent (undeletable, analytics-polled,
 * listed as sent): `sending` while the email dispatch service drains the
 * job's batches (the service then flips it to `completed`,
 * `completed_with_errors` or `failed`); `completed`; or
 * `completed_with_errors` when at least one batch went out but not all did.
 */
export const SENT_SOUTHPAW_JOB_STATUSES = [
  "sending",
  "completed",
  SOUTHPAW_JOB_COMPLETED_WITH_ERRORS,
] as const;

export function isSentSouthpawJobStatus(
  status: string | null | undefined
): boolean {
  const lower = status?.toLowerCase() ?? "";
  return (SENT_SOUTHPAW_JOB_STATUSES as readonly string[]).includes(lower);
}

/** Statuses of a job that has committed to sending (or has sent) — never
 *  `generating_audience` / `failed`. Case-sensitive, unlike
 *  `isSentSouthpawJobStatus`. */
export const MESSAGED_SOUTHPAW_JOB_STATUSES = [
  "scheduled",
  "audience_generated",
  "picked_up",
  "running",
  ...SENT_SOUTHPAW_JOB_STATUSES,
] as const;

export type MessagedSouthpawJobStatus =
  (typeof MESSAGED_SOUTHPAW_JOB_STATUSES)[number];

export function isMessagedSouthpawJobStatus(
  status: string
): status is MessagedSouthpawJobStatus {
  return (MESSAGED_SOUTHPAW_JOB_STATUSES as readonly string[]).includes(status);
}

/** True for a comedian job whose body is plain text, so an edit cannot overwrite a newsletter or rich-body sentinel. */
export function isEditableComedianJob(job: {
  email_template: string | null;
}): boolean {
  return job.email_template === PLAIN_BODY_EMAIL_TEMPLATE;
}

/**
 * No edits of any kind (content, design, or schedule) once the send is this
 * close: the scheduler may pick the job up at any moment, and a half-applied
 * edit racing the send is worse than asking the manager to leave it alone.
 */
export const EDIT_FREEZE_MINUTES = 5;

export function isInsideEditFreeze(time: string | Dayjs): boolean {
  return dayjs(time).isBefore(dayjs().add(EDIT_FREEZE_MINUTES, "minute"));
}
